import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, RotateCcw, AlertTriangle } from "lucide-react";
import PdaLayout from "./PdaLayout";
import { beep, primeAudio } from "@/lib/pdaSounds";
import { usePdaScan } from "@/hooks/usePdaScan";
import usePdaScenarioHint from "@/hooks/usePdaScenarioHint";
import useLiveProductBreakdown from "@/hooks/useLiveProductBreakdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Bulk-delete physical linen from inventory. Same semantics as the desktop
 * "bulk-delete" flow — scan a stack of RFIDs (any product, any status),
 * confirm count, then permanently remove them. After deletion they read as
 * unknown to the system on any future scan.
 */
export default function PdaDelete({ onLogout }) {
  const navigate = useNavigate();
  const [rfids, setRfids] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  usePdaScenarioHint("Delete");
  useEffect(() => { primeAudio(); }, []);

  // Live product breakdown so the operator sees "12 sheets, 6 towels" instead
  // of a wall of EPC codes — same UX requirement as Stocktake / Receiving.
  const { breakdown, unknown } = useLiveProductBreakdown(rfids);

  const rfidsRef = useRef(new Set());
  const clearRfids = useCallback(() => {
    rfidsRef.current = new Set();
    setRfids(new Set());
  }, []);

  const onEpcs = useCallback((epcs) => {
    if (!epcs || epcs.length === 0) return;
    let added = 0;
    for (const e of epcs) {
      if (!rfidsRef.current.has(e)) { rfidsRef.current.add(e); added++; }
    }
    if (added > 0) {
      setRfids(new Set(rfidsRef.current));
      beep.tick();
    }
  }, []);
  usePdaScan(onEpcs, { enabled: true });

  const onSubmit = async () => {
    if (rfids.size === 0 || submitting) return;
    const list = Array.from(rfids);
    if (!window.confirm(`Delete ${list.length} item(s) from inventory permanently? They will read as unknown on any future scan.`)) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/linen/bulk-delete`, { rfids: list, reason: "PDA bulk delete" });
      beep.complete();
      toast.success(`Deleted ${res.data.deleted_count} item(s)${res.data.not_found?.length ? ` · ${res.data.not_found.length} not found` : ""}`);
      clearRfids();
    } catch (e) {
      beep.error();
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Delete failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PdaLayout
      title="Delete"
      subtitle="Retire linen permanently"
      onBack={() => navigate("/pda/menu")}
      onLogout={onLogout}
      footer={
        <button
          onClick={onSubmit}
          disabled={rfids.size === 0 || submitting}
          data-testid="pda-delete-submit"
          className="w-full h-12 rounded-md bg-rose-700 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-40"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Trash2 className="h-5 w-5" /> Delete {rfids.size}</>}
        </button>
      }
    >
      <div className="rounded-md border bg-white px-3 py-4 mb-3 text-center">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500">Scanned to delete</div>
        <div className="font-display font-bold text-5xl text-rose-700 mt-1" data-testid="pda-delete-count">{rfids.size}</div>
        <div className="text-xs text-neutral-500 mt-1">Press the middle key to scan</div>
        {rfids.size > 0 ? (
          <button onClick={clearRfids} data-testid="pda-delete-clear" className="mt-2 text-xs text-neutral-700 underline flex items-center gap-1 mx-auto">
            <RotateCcw className="h-3 w-3" /> Clear
          </button>
        ) : null}
      </div>

      {breakdown.length > 0 && (
        <div className="rounded-md border bg-white mb-2" data-testid="pda-delete-breakdown">
          <div className="px-3 py-2 border-b text-xs uppercase tracking-wider text-neutral-600">By product</div>
          <div className="divide-y">
            {breakdown.map((b) => (
              <div key={b.product_id} className="px-3 py-2 flex items-center justify-between">
                <span className="text-sm font-medium">{b.product_name}</span>
                <span className="font-mono tabular-nums font-bold text-sm">{b.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {unknown.length > 0 && (
        <UnknownCollapsible unknown={unknown} />
      )}
    </PdaLayout>
  );
}

function UnknownCollapsible({ unknown }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border bg-amber-50 border-amber-200" data-testid="pda-delete-unknown">
      <button onClick={() => setOpen((v) => !v)} className="w-full px-3 py-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-amber-900">
          <AlertTriangle className="h-3.5 w-3.5" /> Unknown tags · {unknown.length}
        </span>
        <span className="text-xs text-amber-800">{open ? "Hide codes" : "Show codes"}</span>
      </button>
      {open && (
        <div className="max-h-48 overflow-y-auto divide-y divide-amber-200 border-t border-amber-200">
          {unknown.map((r) => (
            <div key={r} className="px-3 py-1.5 text-[11px] font-mono">{r}</div>
          ))}
        </div>
      )}
    </div>
  );
}
